"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const TrainingInterface_1 = require("../../../BusinessLogicLayer/TrainingInterface");
const dateComparison_1 = __importDefault(require("../../../Util/dateComparison"));
const DateTimeProvider_1 = require("../../../Util/DateTimeProvider");
const asFloat_1 = __importDefault(require("../../../Util/asFloat"));
const inDateRange_1 = __importDefault(require("../../../Util/inDateRange"));
const asyncIterate_1 = __importDefault(require("../../../Util/asyncIterate"));
const arrayFind_1 = __importDefault(require("../../../Util/arrayFind"));
const State_1 = require("../types/State");
class TrainingAdapter {
    constructor(dateTimeProvider, dateTimeBridge) {
        this.adapt = async (systemDate, data, unit) => {
            if (data !== null && data.trainings !== null) {
                return {
                    rate: TrainingAdapter.rating(data.trainings.rating),
                    isDone: (0, State_1.asBool)(data.trained),
                    exercises: this.exercises(systemDate, data.trainings.exercises, unit),
                };
            }
            return {
                rate: null,
                isDone: null,
                exercises: [],
            };
        };
        this.adaptExercise = (_systemDate, exercise, unit) => {
            return {
                id: exercise.id.toString(),
                name: exercise.title,
                guidelines: exercise.description,
                rest: exercise.rest,
                sets: exercise.setsExtended.map((setExtended) => this.adaptSet(setExtended, unit)),
                seriesData: exercise.seriesData,
                rating: this.adaptExerciseRating(exercise.rating),
                historyOfProgress: this.adaptExercisesToProgression(exercise.history, unit),
                note: exercise.note,
                image: "https://portal.personalbodyplan.com/" + exercise.image,
                group: exercise.group || null,
            };
        };
        this.adaptExercisesToProgression = (historyItems, unit) => {
            const ret = [];
            historyItems.forEach((historyItem) => {
                ret.push({
                    sets: historyItem.sets.map((setExtended) => this.adaptSet(setExtended, unit)),
                    date: this.dateTimeBridge.userDate(historyItem.dateTime.iso8601),
                });
            });
            return ret;
        };
        this.adaptSet = (set, unit) => {
            return {
                ordinalNumber: set.no,
                weight: this.weight(set.weight, unit),
                repetitions: set.repetitions,
            };
        };
        this.weight = (kilograms, unit) => {
            if (kilograms === null || unit === "kg") {
                return kilograms;
            }
            return (0, asFloat_1.default)(kilograms * 2.20462262, 2);
        };
        this.adaptDateRange = (days) => {
            return days.map((day) => {
                return {
                    date: day.date.split("T")[0],
                    rate: day.trainings
                        ? TrainingAdapter.rating(day.trainings.rating)
                        : null,
                    isDone: (0, State_1.asBool)(day.trained),
                    dayType: day.dayType,
                    day: this.dateTimeProvider.dayOfWeekRaw(day.date, DateTimeProvider_1.FORMAT_RAW),
                };
            });
        };
        this.mapWeekDay = async (customerId, progressItem, weekPlan) => {
            const training = progressItem.trainings;
            const date = this.dateTimeBridge.userDate(progressItem.date);
            return {
                date,
                rate: training ? TrainingAdapter.rating(training.rating) : null,
                isDone: (0, State_1.asBool)(progressItem.trained),
                dayType: await this.establishDayType(this.dateTimeBridge.systemDate(this.dateTimeProvider.fromDateTimeToDate(date)), customerId, weekPlan),
                day: this.dateTimeProvider.dayOfWeek(date),
            };
        };
        this.mapWeek = async (_week, systemDateRange, customerId, progressItems, weekPlan) => {
            return (await (0, asyncIterate_1.default)(progressItems.filter(({ date }) => {
                const [day] = date.split("T");
                return (0, inDateRange_1.default)(day, systemDateRange);
            }), (item) => this.mapWeekDay(customerId, item, weekPlan))).sort((a, b) => {
                return (0, dateComparison_1.default)(this.dateTimeBridge.systemDate(a.date), this.dateTimeBridge.systemDate(b.date));
            });
        };
        this.mergeDaysWithDayType = (progressItems, days) => {
            return progressItems.map((item) => {
                const [itemDay] = item.date.split("T");
                const day = days.find((day) => {
                    const [weekPlanDay] = day.date.split("T");
                    return (0, dateComparison_1.default)(weekPlanDay, itemDay) === 0;
                });
                const training = item.trainings;
                return {
                    date: itemDay,
                    rate: training ? TrainingAdapter.rating(training.rating) : null,
                    isDone: (0, State_1.asBool)(item.trained),
                    dayType: day.dayType,
                    day: this.dateTimeProvider.dayOfWeek(item.date, "YYYY-MM-DD"),
                };
            });
        };
        this.adaptExerciseRating = (rating) => {
            switch (rating) {
                case 1:
                    return TrainingInterface_1.ExerciseRate.HARD;
                case 5:
                    return TrainingInterface_1.ExerciseRate.MEDIUM;
                case 10:
                    return TrainingInterface_1.ExerciseRate.EASY;
                default:
                    return null;
            }
        };
        this.establishDayType = async (date, _customerId, weekPlan) => {
            const days = weekPlan.days;
            return (0, arrayFind_1.default)(days, ({ date: compDate }) => date === compDate, `week plan for date ${date} was not found `).dayType;
        };
        this.dateTimeProvider = dateTimeProvider;
        this.dateTimeBridge = dateTimeBridge;
    }
    exercises(systemDate, exercises, unit) {
        const adapted = [];
        for (let i = 0; i < exercises.length; i++) {
            adapted.push(this.adaptExercise(systemDate, exercises[i], unit));
        }
        return adapted;
    }
    static rating(rating) {
        return rating === 0 ? null : rating;
    }
}
exports.default = TrainingAdapter;
//# sourceMappingURL=TrainingAdapter.js.map